import { useI18n } from "@wato/i18n/react";
import { Button } from "@wato/ui";
import { useInstallPrompt } from "./useInstallPrompt";

/** The iOS share-sheet glyph (square with an arrow out of its top); no such icon in `@wato/ui`. */
function ShareGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="inline-block size-4 align-text-bottom"
    >
      <path d="M12 15V4m-4 4 4-4 4 4" />
      <path d="M7 11H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-1" />
    </svg>
  );
}

/**
 * OWN-162: «Install app» entry of the profile menu. A button where the browser offers a native
 * install prompt (Chrome/Android/desktop), the «Add to Home Screen» steps on iOS (which has no
 * install API), nothing when the app is already installed or no install route exists.
 */
export function InstallAppMenuItem({ onInstalled }: { readonly onInstalled?: () => void }) {
  const { t } = useI18n();
  const { canInstall, install, isIos, isStandalone } = useInstallPrompt();
  if (isStandalone) return null;

  if (canInstall) {
    return (
      <div className="border-t border-line px-3 py-2">
        <Button
          variant="secondary"
          className="min-h-11"
          onClick={() => {
            void install().then((outcome) => {
              if (outcome === "accepted") onInstalled?.();
            });
          }}
        >
          {t("pwa.install.action")}
        </Button>
      </div>
    );
  }

  if (isIos) {
    return (
      <section
        aria-labelledby="install-ios-title"
        data-testid="install-ios-hint"
        className="border-t border-line px-3 py-2"
      >
        <h3 id="install-ios-title" className="pb-1 text-xs font-semibold text-fg-muted">
          {t("pwa.install.iosTitle")}
        </h3>
        <ol className="list-decimal space-y-1 ps-5 text-sm text-fg-muted">
          <li>
            {t("pwa.install.iosStepShare")} <ShareGlyph />
          </li>
          <li>{t("pwa.install.iosStepAdd")}</li>
        </ol>
      </section>
    );
  }

  return null;
}
